import { planRuns } from "@pluck/api/lib/board/prompt-plan";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { TRPCClientError } from "@trpc/client";
import {
	addEdge,
	Background,
	BackgroundVariant,
	type Connection,
	Controls,
	type Edge,
	MiniMap,
	type Node,
	type OnConnect,
	type OnConnectEnd,
	ReactFlow,
	ReactFlowProvider,
	useEdgesState,
	useNodesState,
	useReactFlow,
	type Viewport,
} from "@xyflow/react";
import {
	ArrowLeft,
	Check,
	ImagePlus,
	Images,
	Loader2,
	MessageSquare,
	Plus,
	Sparkles,
} from "lucide-react";
import {
	type DragEvent,
	type FormEvent,
	type MouseEvent as ReactMouseEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { toast } from "sonner";
import { BoardDataProvider } from "@/components/canvas/board-context";
import { CanvasContextMenu } from "@/components/canvas/canvas-context-menu";
import { ChatPanel } from "@/components/canvas/chat-panel";
import { GenerationNodeView } from "@/components/canvas/generation-node";
import { ImageNodeView } from "@/components/canvas/image-node";
import { LibraryPicker } from "@/components/canvas/library-picker";
import { PluckNodeView } from "@/components/canvas/pluck-node";
import { PromptNodeView } from "@/components/canvas/prompt-node";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { track } from "@/lib/analytics";
import { fetchAssetBase64 } from "@/lib/assets";
import {
	type AspectRatio,
	AUTO_MODEL,
	type BoardNode,
	collectPromptInputs,
	createEdge,
	createGenerationNode,
	createImageNode,
	createPluckNode,
	createPromptNode,
	effectivePromptMode,
	effectivePromptModel,
	generationCardsFor,
	generationInputsFor,
	generationSlotPosition,
	isGenerationNode,
	isImageNode,
	isPluckNode,
	isPromptNode,
	isValidConnection,
	LOCAL_MODEL,
	type PromptInput,
	type PromptMode,
	type PromptNode,
	type QualityTier,
} from "@/lib/board-graph";
import {
	type CanvasState,
	dedupeGenerationCards,
	mergeCanvas,
	reconcileGenerationCards,
	serializeCanvas,
} from "@/lib/board-sync";
import {
	composeGenerationPrompt,
	extractBrief,
	type PromptReference,
	referenceLabel,
} from "@/lib/compose-generation-prompt";
import { getDesktopBridge } from "@/lib/desktop";
import {
	ACCEPTED_UPLOAD_TYPES,
	isAcceptedUploadType,
	prepareImageForUpload,
} from "@/lib/prepare-upload";
import { reportError } from "@/lib/report-error";
import { queryClient, trpc, trpcClient } from "@/lib/trpc";

/** A prompt input resolved to the label and image Codex receives. */
interface GenerationInput {
	assetId: string | null;
	kind: PromptInput["kind"];
	/** Set for plucks; generations aren't recorded as input plucks. */
	pluckId: string | null;
	ref: PromptReference;
}

const nodeTypes = {
	pluck: PluckNodeView,
	prompt: PromptNodeView,
	generation: GenerationNodeView,
	image: ImageNodeView,
};

const AUTOSAVE_DELAY_MS = 800;
/** Prefix the API puts on "not enough credits" errors. */
const INSUFFICIENT_CREDITS = "INSUFFICIENT_CREDITS:";
/** How often an open board checks for changes made in other windows. */
const BOARD_SYNC_POLL_MS = 2000;
const GENERATIONS_POLL_MS = 5000;
/** How often a desktop board window checks for prompts agents asked to run. */
const AGENT_REQUEST_POLL_MS = 3000;
const RUNNING_GENERATIONS_POLL_MS = 3000;
const GENERATION_TIMEOUT_SECONDS = 900;
const DEFAULT_VIEWPORT: Viewport = { x: 80, y: 80, zoom: 0.9 };
const NEW_NODE_SPREAD = 40;
/** Shorter drags than this (in canvas units) count as a click on the handle. */
const MIN_DRAG_TO_CREATE_PX = 24;
/** Roughly half a prompt card's height, so its input handle meets the cursor. */
const PROMPT_HANDLE_OFFSET_Y = 75;
/** Offset between several images dropped or uploaded at once. */
const UPLOAD_CASCADE_PX = 32;

interface FlowPosition {
	x: number;
	y: number;
}

/** Where an "add card" action came from, for analytics. */
type AddVia = "toolbar" | "context_menu" | "empty_state";
type UploadVia = AddVia | "drop";

/** Card counts by type, for analytics (no content). */
function cardCounts(nodes: Node[]) {
	return {
		cards: nodes.length,
		pluck_cards: nodes.filter(isPluckNode).length,
		image_cards: nodes.filter(isImageNode).length,
		prompt_cards: nodes.filter(isPromptNode).length,
		generation_cards: nodes.filter(isGenerationNode).length,
	};
}

function nodeTypesOf(nodes: Node[]): string[] {
	return [...new Set(nodes.map((n) => n.type ?? "unknown"))];
}

function modelKind(model: string): "local" | "auto" | "hosted" {
	if (model === LOCAL_MODEL) {
		return "local";
	}
	return model === AUTO_MODEL ? "auto" : "hosted";
}

/** What a prompt run uses (settings, reference counts), for analytics. */
function runProperties(
	prompt: PromptNode,
	model: string,
	references: { kind: PromptInput["kind"] }[],
	desktop: boolean
) {
	let runner: "hosted" | "local" | "copy" = "hosted";
	if (model === LOCAL_MODEL) {
		runner = desktop ? "local" : "copy";
	}
	const countOf = (kind: PromptInput["kind"]) =>
		references.filter((r) => r.kind === kind).length;
	return {
		runner,
		model,
		model_kind: modelKind(model),
		quality: prompt.data.quality ?? "default",
		aspect: prompt.data.aspect ?? "auto",
		mode: effectivePromptMode(prompt.data),
		references: references.length,
		pluck_references: countOf("pluck"),
		image_references: countOf("image"),
		generation_references: countOf("generation"),
		has_text: prompt.data.text.trim().length > 0,
	};
}

/** One upload batch: how many files were picked, accepted and stored. */
function trackUpload(
	via: UploadVia,
	files: number,
	accepted: number,
	uploaded: number
): void {
	track("upload.completed", {
		via,
		files,
		uploaded,
		failed: accepted - uploaded,
		rejected_type: files - accepted,
	});
	if (uploaded > 0) {
		track("card.added", { type: "image", via, count: uploaded });
	}
}

export function BoardPage() {
	const { boardId } = useParams({ from: "/app/boards/$boardId" });
	const board = useQuery(trpc.boards.get.queryOptions({ id: boardId }));

	if (board.isPending) {
		return (
			<div className="flex h-full flex-col">
				<div className="flex h-12 items-center gap-3 border-b px-4">
					<Skeleton className="h-5 w-48" />
				</div>
				<Skeleton className="m-4 flex-1 rounded-xl" />
			</div>
		);
	}

	if (!board.data) {
		return (
			<div className="p-8">
				<p className="text-muted-foreground">This board doesn't exist.</p>
				<Button asChild className="mt-4" variant="outline">
					<Link to="/boards">Back to boards</Link>
				</Button>
			</div>
		);
	}

	return (
		<ReactFlowProvider>
			<BoardEditor
				boardId={board.data.id}
				initialEdges={board.data.state.edges as Edge[]}
				initialName={board.data.name}
				initialNodes={board.data.state.nodes as BoardNode[]}
				initialRevision={board.data.revision}
				initialViewport={board.data.state.viewport ?? DEFAULT_VIEWPORT}
				key={board.data.id}
			/>
		</ReactFlowProvider>
	);
}

type SaveState = "saved" | "dirty" | "saving";

function SaveIndicator({ state }: { state: SaveState }) {
	if (state === "saving") {
		return (
			<span className="flex items-center gap-1 text-muted-foreground text-xs">
				<Loader2 className="size-3 animate-spin" />
				Saving
			</span>
		);
	}
	if (state === "saved") {
		return (
			<span className="flex items-center gap-1 text-muted-foreground text-xs">
				<Check className="size-3" />
				Saved
			</span>
		);
	}
	return <span className="text-muted-foreground text-xs">Unsaved</span>;
}

function BoardEditor({
	boardId,
	initialName,
	initialNodes,
	initialEdges,
	initialRevision,
	initialViewport,
}: {
	boardId: string;
	initialEdges: Edge[];
	initialName: string;
	initialNodes: BoardNode[];
	initialRevision: number;
	initialViewport: Viewport;
}) {
	const [nodes, setNodes, onNodesChange] = useNodesState<Node>(initialNodes);
	const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(initialEdges);
	const [name, setName] = useState(initialName);
	const [pickerOpen, setPickerOpen] = useState(false);
	const [chatOpen, setChatOpen] = useState(false);
	const [menu, setMenu] = useState<{
		screen: FlowPosition;
		flow: FlowPosition;
	} | null>(null);
	const [uploading, setUploading] = useState(0);
	/** Where the next pluck from the picker goes (set by the context menu). */
	const pendingPluckPosition = useRef<FlowPosition | null>(null);
	/** Where files picked in the upload dialog go. */
	const pendingUploadPosition = useRef<FlowPosition | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [running, setRunning] = useState<Set<string>>(() => new Set());
	const [saveState, setSaveState] = useState<SaveState>("saved");
	const viewportRef = useRef<Viewport>(initialViewport);
	const { screenToFlowPosition, getViewport } = useReactFlow();
	const nodesRef = useRef(nodes);
	const edgesRef = useRef(edges);
	nodesRef.current = nodes;
	edgesRef.current = edges;
	const bridge = getDesktopBridge();

	// Time on the board and what it held, for analytics.
	useEffect(() => {
		const openedAt = Date.now();
		const opened = cardCounts(nodesRef.current);
		track("board.opened", {
			board_id: boardId,
			...opened,
			connections: edgesRef.current.length,
		});
		if (opened.cards === 0) {
			track("onboarding.empty_state_viewed", { surface: "board" });
		}
		return () => {
			track("board.closed", {
				board_id: boardId,
				duration_ms: Date.now() - openedAt,
				...cardCounts(nodesRef.current),
				connections: edgesRef.current.length,
			});
		};
	}, [boardId]);

	// ── Data for nodes ──────────────────────────────────────────────
	const pluckIds = useMemo(
		() => [...new Set(nodes.filter(isPluckNode).map((n) => n.data.pluckId))],
		[nodes]
	);
	const plucks = useQuery(
		trpc.plucks.getMany.queryOptions({ ids: pluckIds }, { enabled: true })
	);
	const generations = useQuery(
		trpc.generations.listForBoard.queryOptions(
			{ boardId },
			{
				// Picks up generations started or finished in another window, and
				// hosted runs progressing on the server.
				refetchInterval: (query) =>
					running.size > 0 ||
					query.state.data?.some(
						(g) => g.status === "queued" || g.status === "running"
					)
						? RUNNING_GENERATIONS_POLL_MS
						: GENERATIONS_POLL_MS,
			}
		)
	);
	const hostedModelsQuery = useQuery(
		trpc.generations.models.queryOptions(undefined, { staleTime: 5 * 60_000 })
	);
	const hostedModels = useMemo(
		() => hostedModelsQuery.data ?? [],
		[hostedModelsQuery.data]
	);
	const billing = useQuery(trpc.billing.summary.queryOptions());
	const generationSettings = useQuery(
		trpc.account.getGenerationSettings.queryOptions()
	);
	const promptTemplate = generationSettings.data?.template ?? "generic";
	const customInstructions = generationSettings.data?.instructions ?? "";
	const navigate = useNavigate();
	const plucksById = useMemo(
		() => new Map((plucks.data ?? []).map((p) => [p.id, p])),
		[plucks.data]
	);
	const generationsById = useMemo(
		() => new Map((generations.data ?? []).map((g) => [g.id, g])),
		[generations.data]
	);
	const uploads = useQuery(trpc.uploads.list.queryOptions());
	const uploadsById = useMemo(
		() => new Map((uploads.data ?? []).map((u) => [u.id, u])),
		[uploads.data]
	);

	// ── Persistence & sync ──────────────────────────────────────────
	// The canvas is shared by every open window (web and desktop). Each save
	// names the revision it was based on; a stale save is rejected, and the
	// window merges the newer state (three-way, against `base`) and saves again.
	// Windows poll the revision so changes elsewhere show up within seconds.
	const revisionRef = useRef(initialRevision);
	const baseRef = useRef<CanvasState>({
		nodes: initialNodes,
		edges: initialEdges,
	});
	const baseSerializedRef = useRef(serializeCanvas(baseRef.current));
	/** One save or pull at a time. */
	const busyRef = useRef(false);
	const serialized = useMemo(
		() => serializeCanvas({ nodes, edges }),
		[nodes, edges]
	);

	const pull = useCallback(async () => {
		const remote = await trpcClient.boards.get.query({ id: boardId });
		if (remote.revision === revisionRef.current) {
			return;
		}
		const remoteCanvas: CanvasState = {
			nodes: remote.state.nodes as Node[],
			edges: remote.state.edges as Edge[],
		};
		// Two windows can each hold a card for the same generation; the merge
		// keeps both, so collapse them here (every window keeps the same one).
		const merged = dedupeGenerationCards(
			mergeCanvas(
				baseRef.current,
				{ nodes: nodesRef.current, edges: edgesRef.current },
				remoteCanvas
			)
		);
		baseRef.current = remoteCanvas;
		baseSerializedRef.current = serializeCanvas(remoteCanvas);
		revisionRef.current = remote.revision;
		setNodes(merged.nodes);
		setEdges(merged.edges);
		// Cards from the other window may reference generations or uploads this
		// window hasn't loaded yet.
		await Promise.all([
			queryClient.invalidateQueries({
				queryKey: trpc.generations.listForBoard.queryKey({ boardId }),
			}),
			queryClient.invalidateQueries({
				queryKey: trpc.uploads.list.queryKey(),
			}),
		]);
	}, [boardId, setNodes, setEdges]);

	const save = useCallback(async (): Promise<void> => {
		if (busyRef.current) {
			// A pull or save is running; try again once it's done.
			setTimeout(() => {
				save().catch(() => setSaveState("dirty"));
			}, AUTOSAVE_DELAY_MS);
			return;
		}
		const snapshot = serializeCanvas({
			nodes: nodesRef.current,
			edges: edgesRef.current,
		});
		if (snapshot === baseSerializedRef.current) {
			setSaveState("saved");
			return;
		}
		busyRef.current = true;
		setSaveState("saving");
		try {
			const { revision } = await trpcClient.boards.saveState.mutate({
				id: boardId,
				state: { ...JSON.parse(snapshot), viewport: viewportRef.current },
				baseRevision: revisionRef.current,
			});
			revisionRef.current = revision;
			baseRef.current = JSON.parse(snapshot) as CanvasState;
			baseSerializedRef.current = snapshot;
		} catch (err) {
			const conflict =
				err instanceof TRPCClientError && err.data?.code === "CONFLICT";
			if (!conflict) {
				setSaveState("dirty");
				return;
			}
			// Another window saved first: merge its changes; the autosave effect
			// then saves the merged canvas against the new revision.
			await pull().catch(() => setSaveState("dirty"));
		} finally {
			busyRef.current = false;
		}
		const current = serializeCanvas({
			nodes: nodesRef.current,
			edges: edgesRef.current,
		});
		if (current === baseSerializedRef.current) {
			setSaveState("saved");
			return;
		}
		// Edits made during the save, or local changes kept by a merge.
		setSaveState("dirty");
		setTimeout(() => {
			save().catch(() => setSaveState("dirty"));
		}, AUTOSAVE_DELAY_MS);
	}, [boardId, pull]);

	useEffect(() => {
		// Skip no-op changes (selection, measurement) and the initial render.
		if (serialized === baseSerializedRef.current) {
			return;
		}
		setSaveState("dirty");
		const timer = setTimeout(() => {
			save().catch(() => setSaveState("dirty"));
		}, AUTOSAVE_DELAY_MS);
		return () => clearTimeout(timer);
	}, [serialized, save]);

	useEffect(() => {
		const check = async () => {
			if (busyRef.current || document.visibilityState !== "visible") {
				return;
			}
			const revision = await trpcClient.boards.revision.query({ id: boardId });
			if (revision === revisionRef.current || busyRef.current) {
				return;
			}
			busyRef.current = true;
			try {
				await pull();
			} finally {
				busyRef.current = false;
			}
			// Save local changes the merge kept (no-op when there are none).
			await save();
		};
		const tick = () => {
			check().catch(() => {
				/* offline or signed out; try again next tick */
			});
		};
		const interval = setInterval(tick, BOARD_SYNC_POLL_MS);
		window.addEventListener("focus", tick);
		document.addEventListener("visibilitychange", tick);
		return () => {
			clearInterval(interval);
			window.removeEventListener("focus", tick);
			document.removeEventListener("visibilitychange", tick);
		};
	}, [boardId, pull, save]);

	// Restore cards for generations that belong to this board but lost their
	// card, and drop duplicate cards two windows may have added.
	const detachedGenerationIds = useRef(new Set<string>());
	useEffect(() => {
		if (!generations.data) {
			return;
		}
		const { add, removeIds } = reconcileGenerationCards(
			nodesRef.current,
			edgesRef.current,
			generations.data.filter((g) => !detachedGenerationIds.current.has(g.id))
		);
		if (add.length === 0 && removeIds.length === 0) {
			return;
		}
		const removed = new Set(removeIds);
		setNodes((ns) => [...ns.filter((n) => !removed.has(n.id)), ...add]);
		setEdges((es) => [
			...es.filter((e) => !(removed.has(e.source) || removed.has(e.target))),
			...add.flatMap((n) => {
				const promptId = generationsById.get(
					(n.data as { generationId: string }).generationId
				)?.nodeId;
				return promptId && nodesRef.current.some((p) => p.id === promptId)
					? [createEdge(promptId, n.id)]
					: [];
			}),
		]);
	}, [generations.data, generationsById, setNodes, setEdges]);

	/** A deleted generation card shouldn't come back: detach it from the board. */
	const detachGenerationCards = useCallback((removed: Node[]) => {
		const removedIds = new Set(removed.map((n) => n.id));
		// Generations that still have another card on the board (a duplicate was
		// deleted) stay attached.
		const stillShown = new Set(
			nodesRef.current
				.filter((n) => !removedIds.has(n.id) && isGenerationNode(n))
				.map((n) => (n.data as { generationId: string }).generationId)
		);
		for (const node of removed) {
			if (isGenerationNode(node) && !stillShown.has(node.data.generationId)) {
				detachedGenerationIds.current.add(node.data.generationId);
				trpcClient.generations.detach
					.mutate({ id: node.data.generationId })
					.catch(() => {
						/* the card may come back on another window; harmless */
					});
			}
		}
	}, []);

	const { mutate: saveViewport } = useMutation(
		trpc.boards.saveViewport.mutationOptions()
	);

	const rename = useMutation(
		trpc.boards.rename.mutationOptions({
			onSuccess: () =>
				queryClient.invalidateQueries({
					queryKey: trpc.boards.list.queryKey(),
				}),
		})
	);
	const submitName = (e: FormEvent) => {
		e.preventDefault();
		const trimmed = name.trim();
		if (trimmed && trimmed !== initialName) {
			rename.mutate({ id: boardId, name: trimmed });
		}
	};

	// ── Graph editing ───────────────────────────────────────────────
	const onConnect: OnConnect = useCallback(
		(connection: Connection) => {
			const types = {
				source_type: nodes.find((n) => n.id === connection.source)?.type,
				target_type: nodes.find((n) => n.id === connection.target)?.type,
			};
			if (!isValidConnection(nodes, connection.source, connection.target)) {
				track("cards.connect_rejected", types);
				toast.error(
					"Connect plucks, images or generations to a prompt, or a prompt to a generation."
				);
				return;
			}
			setEdges((eds) => addEdge(connection, eds));
			track("cards.connected", types);
		},
		[nodes, setEdges]
	);

	// Dragging a pluck, image or generation's handle onto empty canvas creates a
	// prompt there, already connected.
	const onConnectEnd: OnConnectEnd = useCallback(
		(event, state) => {
			if (state.isValid || state.toNode || !state.fromNode) {
				return;
			}
			if (state.fromHandle?.type !== "source") {
				return;
			}
			const from = nodesRef.current.find((n) => n.id === state.fromNode?.id);
			const isReference =
				from &&
				(isPluckNode(from) || isImageNode(from) || isGenerationNode(from));
			if (!(from && isReference)) {
				return;
			}
			// A click on the handle isn't a drag.
			const dragged = Math.hypot(
				state.to.x - state.from.x,
				state.to.y - state.from.y
			);
			if (dragged < MIN_DRAG_TO_CREATE_PX) {
				return;
			}
			const point = "changedTouches" in event ? event.changedTouches[0] : event;
			if (!point) {
				return;
			}
			const drop = screenToFlowPosition({ x: point.clientX, y: point.clientY });
			// Line the prompt's input handle (vertically centred) up with the cursor.
			const prompt = createPromptNode({
				x: drop.x,
				y: drop.y - PROMPT_HANDLE_OFFSET_Y,
			});
			setNodes((ns) => [...ns, prompt]);
			setEdges((es) => [...es, createEdge(from.id, prompt.id)]);
			track("card.added", {
				type: "prompt",
				via: "handle_drag",
				connected_from: from.type,
			});
		},
		[screenToFlowPosition, setNodes, setEdges]
	);

	const centerPosition = useCallback(() => {
		const { innerWidth, innerHeight } = window;
		const jitter = (Math.random() - 0.5) * NEW_NODE_SPREAD;
		return screenToFlowPosition({
			x: innerWidth / 2 + jitter,
			y: innerHeight / 2 + jitter,
		});
	}, [screenToFlowPosition]);

	const addPrompt = (via: AddVia, position?: FlowPosition) => {
		setNodes((ns) => [...ns, createPromptNode(position ?? centerPosition())]);
		track("card.added", { type: "prompt", via });
	};

	const openPicker = (via: AddVia) => {
		setPickerOpen(true);
		track("library_picker.opened", { via });
	};

	const addPluck = (pluckId: string) => {
		const fromMenu = pendingPluckPosition.current !== null;
		const position = pendingPluckPosition.current ?? centerPosition();
		pendingPluckPosition.current = null;
		setNodes((ns) => [...ns, createPluckNode(pluckId, position)]);
		track("card.added", {
			type: "pluck",
			via: fromMenu ? "context_menu" : "library_picker",
			already_on_board: pluckIds.includes(pluckId),
		});
	};

	// ── Uploads ─────────────────────────────────────────────────────
	const uploadFiles = useCallback(
		async (files: File[], origin: FlowPosition, via: UploadVia) => {
			const images = files.filter((f) => isAcceptedUploadType(f.type));
			let uploaded = 0;
			if (images.length < files.length) {
				toast.error("Only PNG, JPEG and WebP images can be uploaded.");
			}
			if (images.length === 0) {
				trackUpload(via, files.length, 0, 0);
				return;
			}
			setUploading((n) => n + images.length);
			try {
				const { limits } = await queryClient.fetchQuery(
					trpc.uploads.limits.queryOptions()
				);
				for (const [index, file] of images.entries()) {
					try {
						// biome-ignore lint/performance/noAwaitInLoops: uploads one image at a time so each lands (and fails) on its own, in order
						const prepared = await prepareImageForUpload(file, limits);
						const row = await trpcClient.uploads.create.mutate(prepared);
						const offset = index * UPLOAD_CASCADE_PX;
						setNodes((ns) => [
							...ns,
							createImageNode(row.id, {
								x: origin.x + offset,
								y: origin.y + offset,
							}),
						]);
						uploaded += 1;
					} catch (err) {
						toast.error(
							`${file.name}: ${err instanceof Error ? err.message : "Upload failed"}`
						);
					} finally {
						setUploading((n) => n - 1);
					}
				}
			} catch (err) {
				setUploading(0);
				toast.error(err instanceof Error ? err.message : "Upload failed");
			} finally {
				trackUpload(via, files.length, images.length, uploaded);
				await Promise.all([
					queryClient.invalidateQueries({
						queryKey: trpc.uploads.list.queryKey(),
					}),
					queryClient.invalidateQueries({
						queryKey: trpc.uploads.limits.queryKey(),
					}),
				]);
			}
		},
		[setNodes]
	);

	/** Which button opened the file dialog, for analytics. */
	const pendingUploadVia = useRef<AddVia>("toolbar");
	const openUploadDialog = (via: AddVia, position?: FlowPosition) => {
		pendingUploadPosition.current = position ?? centerPosition();
		pendingUploadVia.current = via;
		fileInputRef.current?.click();
		track("upload.dialog_opened", { via });
	};

	const deleteUpload = useCallback(
		async (uploadId: string) => {
			try {
				await trpcClient.uploads.delete.mutate({ id: uploadId });
			} catch (err) {
				toast.error(err instanceof Error ? err.message : "Delete failed");
				track("upload.delete_failed");
				return;
			}
			const removed = new Set(
				nodes
					.filter((n) => isImageNode(n) && n.data.uploadId === uploadId)
					.map((n) => n.id)
			);
			setNodes((ns) => ns.filter((n) => !removed.has(n.id)));
			setEdges((es) =>
				es.filter((e) => !(removed.has(e.source) || removed.has(e.target)))
			);
			await queryClient.invalidateQueries({
				queryKey: trpc.uploads.list.queryKey(),
			});
		},
		[nodes, setNodes, setEdges]
	);

	const onDrop = (event: DragEvent<HTMLDivElement>) => {
		const files = [...event.dataTransfer.files];
		if (files.length === 0) {
			return;
		}
		event.preventDefault();
		uploadFiles(
			files,
			screenToFlowPosition({ x: event.clientX, y: event.clientY }),
			"drop"
		).catch(() => {
			/* reported per file */
		});
	};

	const onPaneContextMenu = (event: ReactMouseEvent | MouseEvent) => {
		event.preventDefault();
		setMenu({
			screen: { x: event.clientX, y: event.clientY },
			flow: screenToFlowPosition({ x: event.clientX, y: event.clientY }),
		});
		track("canvas.context_menu_opened");
	};
	const closeMenu = useCallback(() => setMenu(null), []);

	const removeNode = useCallback(
		(nodeId: string) => {
			const removed = nodesRef.current.filter((n) => n.id === nodeId);
			detachGenerationCards(removed);
			track("card.removed", {
				via: "button",
				count: removed.length,
				types: nodeTypesOf(removed),
			});
			setNodes((ns) => ns.filter((n) => n.id !== nodeId));
			setEdges((es) =>
				es.filter((e) => e.source !== nodeId && e.target !== nodeId)
			);
		},
		[setNodes, setEdges, detachGenerationCards]
	);

	/** Keyboard deletes (Backspace/Delete) of cards or connections. */
	const onDelete = useCallback(
		({ nodes: deleted, edges: cut }: { edges: Edge[]; nodes: Node[] }) => {
			if (deleted.length > 0) {
				track("card.removed", {
					via: "keyboard",
					count: deleted.length,
					types: nodeTypesOf(deleted),
				});
			} else if (cut.length > 0) {
				track("cards.disconnected", { via: "keyboard", count: cut.length });
			}
		},
		[]
	);

	const setPromptMode = useCallback(
		(nodeId: string, mode: PromptMode) => {
			track("prompt.mode_changed", { mode });
			setNodes((ns) =>
				ns.map((n) =>
					n.id === nodeId && isPromptNode(n)
						? { ...n, data: { ...n.data, mode } }
						: n
				)
			);
		},
		[setNodes]
	);

	const setPromptSettings = useCallback(
		(
			nodeId: string,
			settings: { aspect?: AspectRatio; quality?: QualityTier }
		) => {
			track("prompt.settings_changed", {
				aspect: settings.aspect,
				quality: settings.quality,
			});
			setNodes((ns) =>
				ns.map((n) =>
					n.id === nodeId && isPromptNode(n)
						? { ...n, data: { ...n.data, ...settings } }
						: n
				)
			);
		},
		[setNodes]
	);

	const setPromptModel = useCallback(
		(nodeId: string, model: string) => {
			track("prompt.model_changed", { model, model_kind: modelKind(model) });
			setNodes((ns) =>
				ns.map((n) =>
					n.id === nodeId && isPromptNode(n)
						? { ...n, data: { ...n.data, model } }
						: n
				)
			);
		},
		[setNodes]
	);

	const promptGenerationInputs = useMemo(
		() =>
			new Map(
				nodes
					.filter(isPromptNode)
					.map((p) => [p.id, generationInputsFor(nodes, edges, p.id)])
			),
		[nodes, edges]
	);

	const updatePromptText = useCallback(
		(nodeId: string, text: string) => {
			setNodes((ns) =>
				ns.map((n) =>
					n.id === nodeId && isPromptNode(n)
						? { ...n, data: { ...n.data, text } }
						: n
				)
			);
		},
		[setNodes]
	);

	// ── Generation ──────────────────────────────────────────────────
	/** Resolve a prompt input to the label and image Codex receives. */
	const resolveInput = useCallback(
		(input: PromptInput): GenerationInput | null => {
			if (input.kind === "pluck") {
				const p = plucksById.get(input.pluckId);
				return p
					? {
							kind: "pluck",
							pluckId: p.id,
							assetId: p.screenshotAssetId,
							ref: {
								rootTag: p.rootTag,
								sourceDomain: p.sourceDomain,
								sourceTitle: p.sourceTitle,
								textSnippet: p.textSnippet ?? undefined,
							},
						}
					: null;
			}
			if (input.kind === "image") {
				const u = uploadsById.get(input.uploadId);
				return u
					? {
							kind: "image",
							pluckId: null,
							assetId: u.assetId,
							ref: { uploadedFileName: u.fileName },
						}
					: null;
			}
			const g = generationsById.get(input.generationId);
			return g?.status === "succeeded" && g.outputAssetId
				? {
						kind: "generation",
						pluckId: null,
						assetId: g.outputAssetId,
						ref: { generatedFromBrief: g.brief || extractBrief(g.prompt) },
					}
				: null;
		},
		[plucksById, uploadsById, generationsById]
	);

	/** One Codex run: registers the generation, adds its card, stores the result. */
	const runGeneration = useCallback(
		async (
			promptNodeId: string,
			prompt: string,
			brief: string,
			references: GenerationInput[],
			position: FlowPosition
		): Promise<string | null> => {
			if (!bridge) {
				return null;
			}
			let generationId: string | null = null;
			try {
				const created = await trpcClient.generations.create.mutate({
					boardId,
					nodeId: promptNodeId,
					prompt,
					brief,
					template: promptTemplate,
					referenceAssetIds: references.flatMap((r) =>
						r.assetId ? [r.assetId] : []
					),
					inputPluckIds: references.flatMap((r) =>
						r.pluckId ? [r.pluckId] : []
					),
				});
				generationId = created.id;

				const genNode = createGenerationNode(created.id, position);
				setNodes((ns) => [...ns, genNode]);
				setEdges((es) => [...es, createEdge(promptNodeId, genNode.id)]);
				await queryClient.invalidateQueries({
					queryKey: trpc.generations.listForBoard.queryKey({ boardId }),
				});

				const attached = await Promise.all(
					references.map(async (r) => {
						if (!r.assetId) {
							return null;
						}
						return {
							label: referenceLabel(r.ref),
							pngBase64: await fetchAssetBase64(r.assetId),
						};
					})
				);

				const result = await bridge.generateImage({
					prompt,
					references: attached.filter((r) => r !== null),
					timeoutSeconds: GENERATION_TIMEOUT_SECONDS,
				});

				if (result.ok) {
					await trpcClient.generations.complete.mutate({
						id: created.id,
						image: {
							pngBase64: result.pngBase64,
							width: result.width,
							height: result.height,
						},
					});
					toast.success("Design generated");
				} else {
					await trpcClient.generations.fail.mutate({
						id: created.id,
						error: result.error,
						log: result.log?.slice(-16_000),
						appVersion: bridge.version,
					});
					toast.error(result.error);
				}
			} catch (err) {
				const message =
					err instanceof Error ? err.message : "Generation failed";
				reportError(err, { kind: "generation", generationId });
				if (generationId) {
					trpcClient.generations.fail
						.mutate({ id: generationId, error: message })
						.catch(() => {
							/* already failing */
						});
				}
				toast.error(message);
			}
			return generationId;
		},
		[bridge, boardId, setNodes, setEdges, promptTemplate]
	);

	// Runs and references per run for each prompt, for cost previews.
	const promptRunShapes = useMemo(() => {
		const shapes = new Map<
			string,
			{ runs: number; referencesPerRun: number }
		>();
		for (const prompt of nodes.filter(isPromptNode)) {
			const resolved = collectPromptInputs(nodes, edges, prompt.id)
				.map(resolveInput)
				.filter((r) => r !== null);
			const runs = planRuns(resolved, prompt.data);
			shapes.set(prompt.id, {
				runs: runs.length,
				referencesPerRun: Math.max(0, ...runs.map((r) => r.references.length)),
			});
		}
		return shapes;
	}, [nodes, edges, resolveInput]);

	/** One hosted run: reserves credits on the server and adds a queued card. */
	const runHosted = useCallback(
		async (
			promptNodeId: string,
			prompt: string,
			brief: string,
			references: GenerationInput[],
			position: FlowPosition,
			model: string,
			settings: {
				aspect?: AspectRatio;
				mode?: PromptMode;
				quality?: QualityTier;
			}
		): Promise<string | null> => {
			try {
				const created = await trpcClient.generations.createHosted.mutate({
					mode: settings.mode,
					boardId,
					nodeId: promptNodeId,
					prompt,
					brief,
					template: promptTemplate,
					model,
					aspect: settings.aspect,
					quality: settings.quality,
					inputPluckIds: references.flatMap((r) =>
						r.pluckId ? [r.pluckId] : []
					),
					referenceAssetIds: references.flatMap((r) =>
						r.assetId ? [r.assetId] : []
					),
				});
				const genNode = createGenerationNode(created.id, position);
				setNodes((ns) => [...ns, genNode]);
				setEdges((es) => [...es, createEdge(promptNodeId, genNode.id)]);
				return created.id;
			} catch (err) {
				const message =
					err instanceof Error ? err.message : "Generation failed";
				if (message.startsWith(INSUFFICIENT_CREDITS)) {
					track("credits.insufficient", {
						model,
						quality: settings.quality,
						balance: queryClient.getQueryData(trpc.billing.summary.queryKey())
							?.credits.balance,
					});
					toast.error(message.slice(INSUFFICIENT_CREDITS.length).trim(), {
						action: {
							label: "Get credits",
							onClick: () => {
								track("credits.cta_clicked", {
									from: "insufficient_credits_toast",
								});
								navigate({ to: "/account" }).catch(() => {
									/* stays on the board */
								});
							},
						},
					});
				} else {
					track("generation.start_failed", { model });
					toast.error(message);
				}
				return null;
			}
		},
		[boardId, setNodes, setEdges, navigate, promptTemplate]
	);

	const generate = useCallback(
		async (
			promptNodeId: string,
			trigger: "user" | "agent" = "user"
		): Promise<string[]> => {
			const promptNode = nodes.find((n) => n.id === promptNodeId);
			if (!(promptNode && isPromptNode(promptNode))) {
				return [];
			}
			const resolved = collectPromptInputs(nodes, edges, promptNodeId)
				.map(resolveInput)
				.filter((r) => r !== null);
			const userPrompt = promptNode.data.text;
			if (resolved.length === 0 && userPrompt.trim().length === 0) {
				track("prompt.run_blocked", { trigger, reason: "empty" });
				toast.error(
					"Write a prompt or connect a pluck, image or generation first."
				);
				return [];
			}

			const runs = planRuns(resolved, promptNode.data);
			const prompts = runs.map((run) =>
				composeGenerationPrompt({
					template: promptTemplate,
					customInstructions,
					userPrompt,
					references: run.references.map((r) => r.ref),
					revisesFirstReference: run.revisesFirst,
				})
			);

			const model = effectivePromptModel(promptNode.data, {
				desktop: bridge !== null,
				hostedAvailable: hostedModels.length > 0,
			});
			track("prompt.run_clicked", {
				...runProperties(promptNode, model, resolved, bridge !== null),
				trigger,
				runs: runs.length,
				template: promptTemplate,
			});
			if (model !== LOCAL_MODEL) {
				const slot = generationCardsFor(nodes, edges, promptNodeId);
				setRunning((s) => new Set(s).add(promptNodeId));
				const ids: string[] = [];
				try {
					// One at a time: the server caps concurrent hosted runs.
					for (const [index, run] of runs.entries()) {
						// biome-ignore lint/performance/noAwaitInLoops: the server caps concurrent hosted runs
						const id = await runHosted(
							promptNodeId,
							prompts[index] ?? "",
							userPrompt,
							run.references,
							generationSlotPosition(promptNode, slot + index),
							model,
							{
								aspect: promptNode.data.aspect,
								mode: effectivePromptMode(promptNode.data),
								quality: promptNode.data.quality,
							}
						);
						if (!id) {
							break;
						}
						ids.push(id);
					}
					return ids;
				} finally {
					setRunning((s) => {
						const next = new Set(s);
						next.delete(promptNodeId);
						return next;
					});
					await Promise.all([
						queryClient.invalidateQueries({
							queryKey: trpc.generations.listForBoard.queryKey({ boardId }),
						}),
						queryClient.invalidateQueries({
							queryKey: trpc.billing.summary.queryKey(),
						}),
					]);
				}
			}

			if (!bridge) {
				track("prompt.copied", { trigger, runs: runs.length });
				await navigator.clipboard.writeText(prompts.join("\n\n---\n\n"));
				toast.success(
					"Prompt copied. Open this board in Pluck Desktop to generate images with Codex."
				);
				return [];
			}

			const firstSlot = generationCardsFor(nodes, edges, promptNodeId);
			setRunning((s) => new Set(s).add(promptNodeId));
			try {
				const results = await Promise.allSettled(
					runs.map((run, index) =>
						runGeneration(
							promptNodeId,
							prompts[index] ?? "",
							userPrompt,
							run.references,
							generationSlotPosition(promptNode, firstSlot + index)
						)
					)
				);
				return results.flatMap((r) =>
					r.status === "fulfilled" && r.value ? [r.value] : []
				);
			} finally {
				setRunning((s) => {
					const next = new Set(s);
					next.delete(promptNodeId);
					return next;
				});
				await queryClient.invalidateQueries({
					queryKey: trpc.generations.listForBoard.queryKey({ boardId }),
				});
			}
		},
		[
			nodes,
			edges,
			resolveInput,
			runGeneration,
			runHosted,
			hostedModels,
			promptTemplate,
			customInstructions,
			bridge,
			boardId,
		]
	);

	// Agents (MCP / chat) ask for prompts to be run; a desktop window with this
	// board open claims each request (only one wins) and runs it here.
	const generateRef = useRef(generate);
	generateRef.current = generate;
	const hostedModelsRef = useRef(hostedModels);
	hostedModelsRef.current = hostedModels;
	useEffect(() => {
		let busy = false;
		const runRequest = async (request: {
			id: string;
			promptNodeId: string;
		}) => {
			// Without Pluck Desktop only prompts set to a hosted model can run here.
			const node = nodesRef.current.find((n) => n.id === request.promptNodeId);
			if (
				!bridge &&
				node &&
				isPromptNode(node) &&
				effectivePromptModel(node.data, {
					desktop: false,
					hostedAvailable: hostedModelsRef.current.length > 0,
				}) === LOCAL_MODEL
			) {
				return;
			}
			const { claimed } = await trpcClient.generationRequests.claim.mutate({
				id: request.id,
			});
			if (!claimed) {
				return;
			}
			const exists = nodesRef.current.some(
				(n) => n.id === request.promptNodeId && isPromptNode(n)
			);
			const generationIds = exists
				? await generateRef.current(request.promptNodeId, "agent")
				: [];
			let error: string | undefined;
			let outcome = "generated";
			if (!exists) {
				error = "That prompt is no longer on the board";
				outcome = "prompt_missing";
			} else if (generationIds.length === 0) {
				error = "Nothing was generated (empty prompt and no references?)";
				outcome = "nothing_generated";
			}
			track("agent_request.completed", {
				outcome,
				generations: generationIds.length,
				desktop: bridge !== null,
			});
			await trpcClient.generationRequests.complete.mutate({
				id: request.id,
				generationIds,
				...(error ? { error } : {}),
			});
		};
		// Runs even while the window is in the background, so an agent in a
		// terminal can drive generation with Pluck minimised.
		const tick = async () => {
			if (busy) {
				return;
			}
			busy = true;
			try {
				const pending =
					await trpcClient.generationRequests.pendingForBoard.query({
						boardId,
					});
				for (const request of pending) {
					// biome-ignore lint/performance/noAwaitInLoops: agent requests run one at a time, in the order they were queued
					await runRequest(request);
				}
			} catch {
				// Offline or signed out; try again next tick.
			} finally {
				busy = false;
			}
		};
		const interval = setInterval(() => {
			tick().catch(() => {
				/* handled inside */
			});
		}, AGENT_REQUEST_POLL_MS);
		return () => clearInterval(interval);
	}, [bridge, boardId]);

	const boardData = useMemo(
		() => ({
			boardId,
			canGenerate: bridge !== null,
			isDesktop: bridge !== null,
			hostedModels,
			creditBalance: billing.data?.credits.balance ?? null,
			promptRunShapes,
			onSetPromptModel: setPromptModel,
			onSetPromptSettings: setPromptSettings,
			promptTemplate,
			plucksById,
			generationsById,
			runningPromptNodeIds: running,
			uploadsById,
			onDeleteUpload: (uploadId: string) => {
				deleteUpload(uploadId).catch(() => {
					/* handled inside */
				});
			},
			onGenerate: (id: string) => {
				generate(id, "user").catch(() => {
					/* handled inside */
				});
			},
			onRemoveNode: removeNode,
			onSetPromptMode: setPromptMode,
			onUpdatePromptText: updatePromptText,
			promptGenerationInputs,
		}),
		[
			boardId,
			bridge,
			plucksById,
			generationsById,
			running,
			uploadsById,
			deleteUpload,
			generate,
			removeNode,
			setPromptMode,
			setPromptModel,
			setPromptSettings,
			promptTemplate,
			updatePromptText,
			promptGenerationInputs,
			promptRunShapes,
			hostedModels,
			billing.data,
		]
	);

	const onBoardPluckIds = useMemo(() => new Set(pluckIds), [pluckIds]);

	return (
		<BoardDataProvider value={boardData}>
			<div className="flex h-full flex-col">
				<header className="desktop-drag flex h-12 shrink-0 items-center gap-2 border-b bg-card px-3">
					<Button asChild size="icon-sm" variant="ghost">
						<Link aria-label="Back to boards" to="/boards">
							<ArrowLeft />
						</Link>
					</Button>
					<form className="min-w-0 flex-1" onSubmit={submitName}>
						<Input
							aria-label="Board name"
							className="h-8 max-w-sm border-transparent bg-transparent font-bold font-display shadow-none hover:border-input focus-visible:border-input"
							onBlur={submitName}
							onChange={(e) => setName(e.target.value)}
							value={name}
						/>
					</form>
					<SaveIndicator state={saveState} />
					{billing.data &&
						(billing.data.plan.id !== "free" ||
							billing.data.credits.balance > 0) && (
							<Link
								className="rounded-full px-2.5 py-1 text-muted-foreground text-xs hover:bg-muted hover:text-foreground"
								onClick={() =>
									track("credits.cta_clicked", { from: "board_header" })
								}
								title="Hosted generation credits"
								to="/account"
							>
								{billing.data.credits.balance.toLocaleString("en-US")} credits
							</Link>
						)}
					<Button
						onClick={() => addPrompt("toolbar")}
						size="sm"
						variant="outline"
					>
						<Sparkles />
						Add prompt
					</Button>
					<Button
						disabled={uploading > 0}
						onClick={() => openUploadDialog("toolbar")}
						size="sm"
						variant="outline"
					>
						{uploading > 0 ? (
							<Loader2 className="animate-spin" />
						) : (
							<ImagePlus />
						)}
						Upload image
					</Button>
					<input
						accept={ACCEPTED_UPLOAD_TYPES.join(",")}
						className="hidden"
						multiple
						onChange={(e) => {
							const files = [...(e.target.files ?? [])];
							e.target.value = "";
							uploadFiles(
								files,
								pendingUploadPosition.current ?? centerPosition(),
								pendingUploadVia.current
							).catch(() => {
								/* reported per file */
							});
						}}
						ref={fileInputRef}
						type="file"
					/>
					<Button
						onClick={() => {
							if (pickerOpen) {
								setPickerOpen(false);
							} else {
								openPicker("toolbar");
							}
						}}
						size="sm"
						variant={pickerOpen ? "default" : "outline"}
					>
						<Images />
						Add plucks
					</Button>
					<Button
						aria-pressed={chatOpen}
						onClick={() => {
							setChatOpen(!chatOpen);
							if (!chatOpen) {
								track("agent_chat.opened", { desktop: bridge !== null });
							}
						}}
						size="sm"
						variant={chatOpen ? "default" : "outline"}
					>
						<MessageSquare />
						Chat
					</Button>
				</header>

				<div className="flex min-h-0 flex-1">
					<div className="relative min-w-0 flex-1">
						<ReactFlow
							defaultViewport={initialViewport}
							deleteKeyCode={["Backspace", "Delete"]}
							edges={edges}
							fitView={false}
							minZoom={0.1}
							nodes={nodes}
							nodeTypes={nodeTypes}
							onConnect={onConnect}
							onConnectEnd={onConnectEnd}
							onDelete={onDelete}
							onDragOver={(e) => {
								if (e.dataTransfer.types.includes("Files")) {
									e.preventDefault();
									e.dataTransfer.dropEffect = "copy";
								}
							}}
							onDrop={onDrop}
							onEdgesChange={onEdgesChange}
							onMoveEnd={() => {
								viewportRef.current = getViewport();
								saveViewport({ id: boardId, viewport: viewportRef.current });
							}}
							onNodesChange={onNodesChange}
							onNodesDelete={detachGenerationCards}
							onPaneContextMenu={onPaneContextMenu}
							proOptions={{ hideAttribution: true }}
						>
							<Background gap={24} variant={BackgroundVariant.Dots} />
							<Controls showInteractive={false} />
							<MiniMap pannable zoomable />
						</ReactFlow>
						{nodes.length === 0 && (
							<div className="pointer-events-none absolute inset-0 flex items-center justify-center">
								<div className="pointer-events-auto flex max-w-sm flex-col items-center gap-3 rounded-xl border bg-card/90 p-6 text-center shadow-sm backdrop-blur">
									<p className="font-medium">Start mixing</p>
									<p className="text-muted-foreground text-sm">
										Add plucks from your library or upload images, add a prompt,
										and connect them into it. Generate turns the mix into a new
										design. Right-click the canvas for quick actions.
									</p>
									<div className="flex gap-2">
										<Button
											onClick={() => openPicker("empty_state")}
											size="sm"
											variant="outline"
										>
											<Plus />
											Add plucks
										</Button>
										<Button onClick={() => addPrompt("empty_state")} size="sm">
											<Sparkles />
											Add prompt
										</Button>
									</div>
								</div>
							</div>
						)}
					</div>
					{menu && (
						<CanvasContextMenu
							onAddPluck={() => {
								pendingPluckPosition.current = menu.flow;
								openPicker("context_menu");
							}}
							onAddPrompt={() => addPrompt("context_menu", menu.flow)}
							onClose={closeMenu}
							onUploadImage={() => openUploadDialog("context_menu", menu.flow)}
							x={menu.screen.x}
							y={menu.screen.y}
						/>
					)}
					{chatOpen && (
						<ChatPanel boardId={boardId} onClose={() => setChatOpen(false)} />
					)}
					{pickerOpen && (
						<LibraryPicker
							onAdd={addPluck}
							onBoardPluckIds={onBoardPluckIds}
							onClose={() => setPickerOpen(false)}
						/>
					)}
				</div>
			</div>
		</BoardDataProvider>
	);
}
